/**
 * PaymentsListPage (11F.7) — tenant-wide payments list at `/payments`.
 *
 * Reference: `Payments.png`. Previously there was no payments surface at
 * all outside an invoice's own detail view, so an admin could not answer
 * "what came in this week?" without opening invoices one by one.
 *
 * A row click navigates to the payment's **invoice**, not to a payment
 * detail page. That is deliberate: every action you might take on a payment
 * (void it, refund it, see what it settled) needs the invoice's context, and
 * a standalone payment page would just be a weaker copy of it. Payments with
 * no linked invoice — none exist today, but the schema permits it — fall
 * back to a toast rather than a dead click.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PageContainer } from '@/components/layout';
import { Card } from '@/components/common/Card';
import { EntityTable } from '@/components/entity';
import { useTenantAwareEntityList } from '@/components/entity/useTenantAwareEntityList';
import { paymentsTableConfig } from '@features/invoices/config/paymentsEntityConfig';
import type { PaymentResponse } from '@features/invoices/api/billing';
import type { EntityActionKey } from '@/components/entity/types';

const PAGE_SIZE = 20;

export const PaymentsListPage: React.FC = () => {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(paymentsTableConfig.defaultSort ?? 'payment_date');
  const [order, setOrder] = useState<'asc' | 'desc'>(
    paymentsTableConfig.defaultOrder ?? 'desc',
  );
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>(
    paymentsTableConfig.defaultFilters ?? {},
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const queryParams = {
    status: filters.status && filters.status !== 'all' ? filters.status : undefined,
    search: search || undefined,
    sort,
    order,
    page,
    page_size: PAGE_SIZE,
  };

  const {
    data: result,
    isLoading,
    error,
    refetch,
    isAllMode,
    focusRowTenant,
  } = useTenantAwareEntityList<PaymentResponse>({
    queryKey: ['payments', 'list', queryParams],
    apiPath: paymentsTableConfig.apiPath,
    params: queryParams as unknown as Record<
      string,
      string | number | boolean | undefined | null
    >,
  });

  const openPayment = (payment: PaymentResponse) => {
    if (!payment.invoice_id) {
      toast('This payment is not linked to an invoice.');
      return;
    }
    focusRowTenant(payment);
    navigate(`/invoices/${payment.invoice_id}`);
  };

  const handleRowAction = (action: EntityActionKey, payment: PaymentResponse) => {
    if (action === 'view') openPayment(payment);
  };

  const total = result?.total ?? 0;
  const subtitle = isLoading
    ? 'Loading…'
    : `${total.toLocaleString('en-IN')} payment${total === 1 ? '' : 's'}`;

  return (
    <PageContainer
      title="Payments"
      subtitle={subtitle}
      data-testid="candeza-payments-list-page"
    >
      <Card padding="lg">
        <EntityTable
          config={paymentsTableConfig}
          result={result}
          isLoading={isLoading}
          error={error as Error | null}
          sort={sort}
          order={order}
          search={search}
          filters={filters}
          page={page}
          selectedIds={selectedIds}
          onSortChange={(s, o) => {
            setSort(s);
            setOrder(o);
            setPage(1);
          }}
          onSearchChange={(v) => {
            setSearch(v);
            setPage(1);
          }}
          onFilterChange={(key, value) => {
            setFilters((prev) => ({ ...prev, [key]: value }));
            setPage(1);
          }}
          onPageChange={setPage}
          onSelectChange={setSelectedIds}
          onRowClick={openPayment}
          onRowAction={handleRowAction}
          onRetry={() => refetch()}
          isAllMode={isAllMode}
        />
      </Card>
    </PageContainer>
  );
};

export default PaymentsListPage;
